/**
 * @file DeviceSwitch.tsx
 * @description Переключатель ширины предпросмотра сайта: телефон / планшет /
 * компьютер — иконками, выбранная подсвечена (вынесен из редактора страниц
 * `SitePageBuilderPage`, phase19.8; общий с экранами «Сайт → …» phase19.16
 * Р1, uiux-design-r1 §1 «[360][768][1280]»).
 *
 * Ширины — реестром `DEVICE_META` (OCP): экран передаёт свой набор.
 * Редактор страниц — `[360, 768, "full"]` (компьютер — во всю колонку, без
 * масштаба), экраны вида сайта — `[360, 768, 1280, 1920]` (сайт своей ширины,
 * уменьшенный под колонку; 1920 — SB120 этап 2). Группа радиокнопок со стрелками; кнопка 48px
 * до 1024 (касание), 40px с 1024.
 */

import { Monitor, Smartphone, Tablet, Tv, type LucideIcon } from "lucide-react";
import type { KeyboardEvent } from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { cn } from "@/lib/utils";

/** Ширина предпросмотра: px или «во всю ширину колонки». */
export type PreviewDevice = 360 | 768 | 1280 | 1920 | "full";

const DEVICE_META: Readonly<Record<PreviewDevice, { icon: LucideIcon; label: string }>> = {
  360: { icon: Smartphone, label: "Телефон 360" },
  768: { icon: Tablet, label: "Планшет 768" },
  1280: { icon: Monitor, label: "Компьютер 1280" },
  // Широкий экран (SB120 этап 2): рамка сайта и «Фон за рамку сайта» видны только шире 1280.
  1920: { icon: Tv, label: "Широкий экран 1920" },
  full: { icon: Monitor, label: "Компьютер, во всю ширину" },
};

interface Props<T extends PreviewDevice> {
  devices: readonly T[];
  value: T;
  onChange: (device: T) => void;
  className?: string;
}

export function DeviceSwitch<T extends PreviewDevice>({ devices, value, onChange, className }: Props<T>) {
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const index = devices.indexOf(value);
    const next = devices[(index + step + devices.length) % devices.length];
    if (next !== undefined) onChange(next);
  }

  return (
    <div role="radiogroup" aria-label="Ширина предпросмотра" onKeyDown={onKeyDown} className={cn("flex gap-1", className)}>
      {devices.map((device) => {
        const meta = DEVICE_META[device];
        const Icon = meta.icon;
        const active = device === value;
        return (
          // Подпись устройства — подсказкой (§7.15: новичок не угадывает значки).
          <Tooltip key={String(device)}>
            <TooltipTrigger
              render={(props) => (
                <button
                  {...props}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={meta.label}
                  tabIndex={active ? 0 : -1}
                  onClick={() => onChange(device)}
                  className={cn(
                    "inline-flex size-12 items-center justify-center rounded-lg outline-none transition-colors lg:size-10",
                    "focus-visible:ring-2 focus-visible:ring-ring",
                    active ? "bg-primary/15 text-on-surface" : "text-on-surface-muted hover:text-on-surface",
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                </button>
              )}
            />
            <TooltipContent>{meta.label}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
